/**
 * Brera · Productos hermanos en la tarjeta (snippets/card-swatches.liquid + snippets/sibling-swatch.liquid)
 * Al pasar el mouse (o con el foco) por el swatch de un hermano, cambia el contenido de la tarjeta por la del hermano
 * (/products/<handle>?view=brera-card, plantilla templates/product.brera-card.liquid): foto, nombre, precio,
 * estrellas, insignias y agregado rápido.
 * Para que el cambio sea inmediato:
 * - Precarga: apenas el mouse entra a la tarjeta (o el dedo toca un swatch) se piden las tarjetas de TODOS sus
 *   hermanos y se descargan sus fotos. Cuando el mouse llega al swatch, ya está todo listo.
 * - Las tarjetas y fotos traídas se guardan para no pedirlas dos veces.
 * - El cambio espera a que la foto nueva esté lista (máx. 600 ms) para que no se vea un hueco en blanco.
 * En pantallas táctiles el primer toque cambia la tarjeta y el segundo abre el producto.
 * Solo actúa dentro de una tarjeta ([data-product-card]). En la ficha (snippets/product-siblings.liquid) los
 * swatches son enlaces normales: el toque abre el otro color directo.
 */
(() => {
  if (window.BreraSiblings) return;

  const cache = new Map();
  const canHover = window.matchMedia('(hover: hover)');

  // Descarga las fotos de la tarjeta traída (mismo srcset/sizes que tendrán en la página) y queda en caché
  const preloadImages = (card) => {
    const imgs = [...card.querySelectorAll('img')];
    imgs.forEach((img) => img.setAttribute('loading', 'eager'));
    const first = imgs[0];
    if (!first) return Promise.resolve();
    const pre = new Image();
    if (first.sizes) pre.sizes = first.sizes;
    if (first.srcset) pre.srcset = first.srcset;
    pre.src = first.src;
    imgs.slice(1).forEach((img) => {
      const p = new Image();
      if (img.sizes) p.sizes = img.sizes;
      if (img.srcset) p.srcset = img.srcset;
      p.src = img.src;
    });
    return (pre.decode ? pre.decode() : Promise.resolve()).catch(() => {});
  };

  const getCard = (url) => {
    if (!cache.has(url)) {
      const u = new URL(url, window.location.origin);
      u.searchParams.set('view', 'brera-card');
      cache.set(url, fetch(u.toString())
        .then((r) => (r.ok ? r.text() : Promise.reject(r.status)))
        .then((html) => {
          const card = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-product-card]');
          if (!card) return null;
          return { card, ready: preloadImages(card) };
        })
        .catch(() => { cache.delete(url); return null; }));
    }
    return cache.get(url);
  };

  const prefetchCard = (card) => {
    if (!card || card.dataset.siblingsPrefetched) return;
    card.dataset.siblingsPrefetched = '1';
    card.querySelectorAll('.sibling-swatch:not(.is-current)').forEach((s) => getCard(s.dataset.siblingUrl));
  };

  // Swatch de hermano que está dentro de una tarjeta (fuera de una tarjeta no se toca)
  const cardSwatch = (target) => {
    const swatch = target.closest && target.closest('.sibling-swatch');
    return swatch && swatch.closest('[data-product-card]') ? swatch : null;
  };

  const wait = (ms) => new Promise((res) => setTimeout(res, ms));

  const swap = async (swatch) => {
    const card = swatch.closest('[data-product-card]');
    if (!card || swatch.classList.contains('is-current')) return;
    const url = swatch.dataset.siblingUrl;
    card.dataset.siblingTarget = url;
    prefetchCard(card);
    const entry = await getCard(url);
    if (!entry || card.dataset.siblingTarget !== url) return;
    await Promise.race([entry.ready, wait(600)]);
    if (card.dataset.siblingTarget !== url) return;
    const fresh = entry.card;
    // Orden de los colores antes del cambio: la tarjeta nueva los muestra en ese mismo orden, así cada color queda en
    // su sitio (el que está bajo el mouse sigue siendo el que se eligió) aunque la lista de cada producto venga en
    // otro orden (por ejemplo, cada producto se pone a sí mismo de primero).
    const order = [...card.querySelectorAll('.sibling-swatch')].map((s) => s.dataset.siblingUrl);
    card.className = fresh.className;
    card.dataset.handle = fresh.dataset.handle;
    card.setAttribute('style', fresh.getAttribute('style') || '');
    card.innerHTML = fresh.innerHTML;
    const list = card.querySelector('[data-siblings]');
    if (list && order.length) {
      const more = list.querySelector('.card-swatches__more');
      [...list.querySelectorAll('.sibling-swatch')]
        .map((s) => ({ s, i: order.indexOf(s.dataset.siblingUrl) }))
        .sort((x, y) => (x.i < 0 ? 999 : x.i) - (y.i < 0 ? 999 : y.i))
        .forEach(({ s }) => list.insertBefore(s, more));
    }
    // Los hermanos de la tarjeta nueva (casi todos ya están en caché; se completa el que falte)
    delete card.dataset.siblingsPrefetched;
    prefetchCard(card);
    card.dispatchEvent(new CustomEvent('sibling:change', { bubbles: true, detail: { url } }));
  };

  // Al cambiar la tarjeta, el swatch nuevo que queda bajo el mouse dispara otro "pointerover" aunque el mouse no se
  // haya movido. Se ignora mientras el puntero siga en el mismo sitio, para que la tarjeta no salte entre colores.
  let lastSwap = null;

  // Precarga al entrar a una tarjeta que tiene hermanos
  document.addEventListener('pointerover', (e) => {
    const card = e.target.closest && e.target.closest('[data-product-card]');
    if (card && card.querySelector('[data-siblings]')) prefetchCard(card);
    const swatch = cardSwatch(e.target);
    if (!swatch || !canHover.matches) return;
    const host = swatch.closest('[data-product-card]');
    if (lastSwap && lastSwap.card === host && Math.abs(e.clientX - lastSwap.x) < 4 && Math.abs(e.clientY - lastSwap.y) < 4) return;
    if (!swatch.classList.contains('is-current')) lastSwap = { card: host, x: e.clientX, y: e.clientY };
    swap(swatch);
  });
  // En celular: el primer contacto con un swatch ya empieza a traer los hermanos
  document.addEventListener('pointerdown', (e) => {
    const swatch = cardSwatch(e.target);
    if (swatch) prefetchCard(swatch.closest('[data-product-card]'));
  }, { passive: true });
  document.addEventListener('focusin', (e) => {
    const swatch = cardSwatch(e.target);
    if (swatch) swap(swatch);
  });
  document.addEventListener('click', (e) => {
    const swatch = cardSwatch(e.target);
    if (!swatch || canHover.matches || swatch.classList.contains('is-current')) return;
    e.preventDefault();
    swap(swatch);
  });

  window.BreraSiblings = { swap, prefetch: prefetchCard };
})();
